/**
 * HALPE-26 İSKELET TANIMI
 * ===========================================================================
 * RTMPose-m'in ürettiği keypoint setinin tek kaynağı. Hem telefon tarafındaki
 * çizim hem de ileride TV alıcısına gidecek mesajlar bu dosyadaki indeksleri
 * ve bağlantıları kullanır.
 *
 * ⚠️ Bu klasördeki modüller uygulama DIŞINDAN da import ediliyor: göreli
 * importlarda `.ts` uzantısı yazın, `@/` alias'ı KULLANMAYIN.
 *
 * Neden MediaPipe-33 değil: RTMPose zaten Halpe-26 üretiyor. 33 yuvaya
 * genişletmek için bilek noktasını parmaklara kopyalamak gerekir; olmayan
 * veriyi varmış gibi göstermek hem boşuna bant genişliği hem de skorlamada
 * yanıltıcı. Halpe-26'nın MediaPipe'ta olmayan bir avantajı da var: gerçek
 * topuk ve ayak ucu noktaları — dans skorlamasında adım tespiti için birebir.
 */

/** Keypoint sayısı — native taraftaki NUM_KEYPOINTS ile aynı olmalı. */
export const KEYPOINT_COUNT = 26;

/** Düz dizide keypoint başına kaç sayı var: x, y, score. */
export const STRIDE = 3;

/** Halpe-26 eklem indeksleri. */
export const KP = {
  nose: 0,
  leftEye: 1,
  rightEye: 2,
  leftEar: 3,
  rightEar: 4,
  leftShoulder: 5,
  rightShoulder: 6,
  leftElbow: 7,
  rightElbow: 8,
  leftWrist: 9,
  rightWrist: 10,
  leftHip: 11,
  rightHip: 12,
  leftKnee: 13,
  rightKnee: 14,
  leftAnkle: 15,
  rightAnkle: 16,
  head: 17,
  neck: 18,
  pelvis: 19,
  leftBigToe: 20,
  rightBigToe: 21,
  leftSmallToe: 22,
  rightSmallToe: 23,
  leftHeel: 24,
  rightHeel: 25,
} as const;

/**
 * Çizilecek kemikler. Yüz detayı (göz/kulak) bilinçli olarak DIŞARIDA:
 * TV mesafesinden görünmüyor, sadece görsel gürültü yapıyor.
 */
export const BONES: readonly (readonly [number, number])[] = [
  // Gövde
  [KP.head, KP.neck],
  [KP.neck, KP.leftShoulder],
  [KP.neck, KP.rightShoulder],
  [KP.leftShoulder, KP.rightShoulder],
  [KP.neck, KP.pelvis],
  [KP.pelvis, KP.leftHip],
  [KP.pelvis, KP.rightHip],
  [KP.leftHip, KP.rightHip],
  // Kollar
  [KP.leftShoulder, KP.leftElbow],
  [KP.leftElbow, KP.leftWrist],
  [KP.rightShoulder, KP.rightElbow],
  [KP.rightElbow, KP.rightWrist],
  // Bacaklar
  [KP.leftHip, KP.leftKnee],
  [KP.leftKnee, KP.leftAnkle],
  [KP.rightHip, KP.rightKnee],
  [KP.rightKnee, KP.rightAnkle],
  // Ayaklar — dans için önemli, adım tespiti buradan çıkacak
  [KP.leftAnkle, KP.leftHeel],
  [KP.leftAnkle, KP.leftBigToe],
  [KP.leftHeel, KP.leftBigToe],
  [KP.rightAnkle, KP.rightHeel],
  [KP.rightAnkle, KP.rightBigToe],
  [KP.rightHeel, KP.rightBigToe],
];

/** Nokta olarak çizilecek eklemler — kemik uçlarıyla aynı küme. */
export const JOINTS: readonly number[] = [
  KP.head,
  KP.neck,
  KP.leftShoulder,
  KP.rightShoulder,
  KP.leftElbow,
  KP.rightElbow,
  KP.leftWrist,
  KP.rightWrist,
  KP.pelvis,
  KP.leftHip,
  KP.rightHip,
  KP.leftKnee,
  KP.rightKnee,
  KP.leftAnkle,
  KP.rightAnkle,
  KP.leftHeel,
  KP.rightHeel,
  KP.leftBigToe,
  KP.rightBigToe,
];

/**
 * Bir keypoint'in ÇİZİLMESİ için gereken en düşük güven.
 *
 * Çizim eşiği ile skorlama eşiği AYRI tutulmalı: burası "kullanıcı görsün mü"
 * sorusu, skorlama tarafı "bu veriye güvenip puan vereyim mi" sorusu. İkisini
 * tek sabite bağlarsanız görseli iyileştirmek için yaptığınız her ayar
 * skorlamayı da sessizce değiştirir.
 */
export const DRAW_SCORE_MIN = 0.3;

/** Düz dizideki k'ıncı keypoint'in x değeri. */
export const kpX = (kp: readonly number[], k: number): number =>
  kp[k * STRIDE] ?? 0;

/** Düz dizideki k'ıncı keypoint'in y değeri. */
export const kpY = (kp: readonly number[], k: number): number =>
  kp[k * STRIDE + 1] ?? 0;

/** Düz dizideki k'ıncı keypoint'in güveni. */
export const kpScore = (kp: readonly number[], k: number): number =>
  kp[k * STRIDE + 2] ?? 0;

/** Düz dizi geçerli bir poz taşıyor mu (boş sonuç değil mi). */
export const hasPose = (kp: readonly number[]): boolean =>
  kp.length === KEYPOINT_COUNT * STRIDE;
